/**
 * Menu / Live Vision API (web). Mirrors apps/WunderGuide/api/menuApi.ts.
 *
 * Direct fast-path scan: POST /chat/v1/{session}/scan runs the vision tool
 * directly (light enrichment, NO tool-selection/synthesis LLM hops) so the
 * companion feed feels responsive. `mode` picks the tool — 'menu' ranks a menu,
 * 'scene' identifies anything (auto-routes menus too). The resulting
 * menu_recommendations / scene_info cards are returned inline AND persisted to
 * history so they replay on refresh.
 *
 * Gated server-side by dining_concierge / live_vision; callers should also gate
 * the entry UI via useFeatureFlags().
 */
import { httpClient } from '../http';
import type { SendChatMessageResponse, ChatMessageItem } from './chatApi';

export interface InlineImage {
  data: string; // base64, no data: prefix
  mime?: string;
  url?: string;
}

export interface ScanOptions {
  message?: string;
  language?: string;
  location?: { latitude: number; longitude: number };
  /** Reverse-geocoded label (e.g. "Waikīkī, Honolulu") for sharper local context. */
  place?: string;
  /** Normalized [0..1] point the user tapped, so the model identifies that object. */
  focusPoint?: { x: number; y: number };
}

export interface ScanResult {
  response: string;
  content: ChatMessageItem[];
  interactionId: string | null;
  latencyMs: number;
}

export interface NearbyPoi {
  name: string;
  lat: number;
  lng: number;
  category?: string | null;
}

async function runScan(
  sessionId: string,
  mode: 'menu' | 'scene',
  image: InlineImage,
  options: ScanOptions = {},
): Promise<ScanResult> {
  const { data } = await httpClient.post<SendChatMessageResponse>(
    `/chat/v1/${sessionId}/scan`,
    {
      image,
      mode,
      message: options.message,
      location: options.location,
      place: options.place,
      focus_point: options.focusPoint,
      language: options.language || 'en-US',
    },
    { timeout: 45000 },
  );
  return {
    response: data.response,
    content: data.content || [],
    interactionId: data.interaction_id ?? null,
    latencyMs: data.latency_ms ?? 0,
  };
}

/**
 * Interest/intent-driven nearby POIs for the live-view map. The server enriches
 * the session (interests + current intent) and runs a relevant places search.
 * Best-effort: returns [] on failure.
 */
export async function getNearbyPois(
  sessionId: string,
  lat: number,
  lng: number,
  mode: 'menu' | 'scene',
): Promise<NearbyPoi[]> {
  try {
    const { data } = await httpClient.get<{ places?: NearbyPoi[] }>(
      `/chat/v1/${sessionId}/nearby`,
      { params: { lat, lng, mode }, timeout: 12000 },
    );
    return (data?.places || []).filter(
      p => typeof p.lat === 'number' && typeof p.lng === 'number',
    );
  } catch {
    return [];
  }
}

/** Scan anything (Live Vision universal lens). */
export function scanScene(
  sessionId: string,
  image: InlineImage,
  options: ScanOptions = {},
): Promise<ScanResult> {
  return runScan(sessionId, 'scene', image, options);
}

/** Scan a menu specifically (Dining Concierge). */
export function scanMenu(
  sessionId: string,
  image: InlineImage,
  options: ScanOptions = {},
): Promise<ScanResult> {
  return runScan(sessionId, 'menu', image, options);
}

export const menuApi = { scanScene, scanMenu, getNearbyPois };
export default menuApi;
